/**
 * PropagationModal — v0.14 R3b (BILL TO redesign)
 *
 * Generic "how should this change propagate?" modal used by the R4 BILL TO
 * panel after the user changes any BILL TO field that has a propagation
 * choice (DBA / address / legal_name / recipient add / recipient remove /
 * star toggle).
 *
 * Backend contract: R3a (v0.14). The modal selects a `FieldUpdateMode`
 * value (mirrors backend schemas/invoices.py FieldUpdateMode Literal) and
 * the caller is responsible for sending the PUT /api/invoices/{id} (or
 * the recipient endpoint) with that mode in the body.
 *
 * Caller responsibilities (NOT this component's job):
 *  - Decide which modes are legal given the GET meta blocks (scope_state,
 *    recipients_meta, etc.) — pass via `availableModes`.
 *  - Pick the right default radio for the field — pass via `defaultMode`.
 *  - Send the PUT, and on 409 `scope_invoiced` re-render this modal with
 *    an updated `availableModes` (drop `save_to_scope`) and a refreshed
 *    `defaultMode`.
 *  - Pass `field_update_mode` THROUGH the mutation hook to the network
 *    request body. Per feedback_mutation_hook_field_drift.md, explicit-pick
 *    mutation hooks silently drop new schema fields. Make sure the
 *    invoice/recipient mutation passes the whole update payload.
 *
 * Edge case: when `field === 'legal_name'` and `availableModes` only has
 * a single entry (`save_to_establishment`), the modal renders as a single
 * confirm step (no radio group), since there is no actual choice.
 *
 * Edge case: when `field === 'star_toggle'`, the modal is single-confirm
 * with simplified copy keyed off `starToggleAction`. No radio group.
 *
 * Sources:
 *  - meta-bus/operations/projects/buildgravity-platform/v0.14-bill-to/R1-R3-design-2026-05-25.md §R3 d
 *  - meta-bus/operations/projects/buildgravity-platform/v0.14-bill-to/R4-bill-to-mockups-2026-05-25.md (Variant A.5)
 *  - meta-bus/operations/projects/buildgravity-platform/v0.14-bill-to/christa-accounting-contacts-mining-2026-05-25.md
 *  - backend/schemas/invoices.py FieldUpdateMode
 *  - backend/routers/invoices.py per-field legality
 */
import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Info, Loader2 } from 'lucide-react';
import { RoleBadge } from './RoleBadge';

// ---------- Public types (LOCKED — R4 builds against this) ----------

export type FieldUpdateMode =
  | 'invoice_only'
  | 'save_to_scope'
  | 'create_new_scope'
  | 'save_to_project'
  | 'save_to_organization'
  | 'save_to_establishment'
  | 'save_dba_default'
  | 'save_org_default'
  // SPEC-christa-contacts-identity R1: "This project, going forward" for a recipient add. It saves the person
  // for THIS project only, in one request (the old save_dba_default option was this, composed of two writes).
  | 'save_project_default'
  | 'stop_for_dba'
  | 'stop_for_org';

export type PropagationField =
  | 'establishment'
  | 'address'
  | 'legal_name'
  | 'recipient_add'
  | 'recipient_remove'
  | 'star_toggle';

export interface PropagationContext {
  project_name?: string;
  establishment_name?: string; // current DBA name (for the invoice's current state)
  organization_name?: string;
  affected_invoice_count?: number;
  scope_is_invoiced?: boolean;
  sibling_scope_count?: number;
}

/** What a recipient add also needs to ask her (R1 AC10-AC12): the person's role and who is saved for the project. */
export interface RecipientAddInfo {
  /** The person being added (the name only; the email is in `newValue`). */
  personName: string;
  /** R5 AC16 (ii): their email, shown beside their name so she can catch a typo before it is saved for the project. */
  personEmail?: string;
  /** "A-197 Hotel Renovation". Only set when the invoice belongs to a project, so the project option exists. */
  projectLabel?: string;
  /** TO when the invoice has no TO yet, CC otherwise. */
  defaultRole: 'to' | 'cc';
  /** Other people on this invoice who are saved client contacts, so they can be saved for the project. */
  savedPeople: Array<{ contact_id: string; name: string; email: string; role: 'to' | 'cc' }>;
  /** People typed for this invoice only with NO email. They stay on this invoice and are never saved. */
  invoiceOnlyPeople: Array<{ name: string; email: string; role: 'to' | 'cc' }>;
  /**
   * R2 AC14: people typed in for this invoice WITH an email. Each gets a tick box (ticked by default) and is saved as a
   * client contact too, so she can save them for the project from this list. `row_id` is their invoice_contacts row.
   */
  oneOffPeople?: Array<{ row_id: string; name: string; email: string; role: 'to' | 'cc' }>;
  /**
   * R2 AC14: set when the person is ALREADY on the invoice as a one-off ("Save for this project" on their row). The
   * role they have is kept, so there is no role question; the title says "Save ... for ...".
   */
  promote?: { rowId: string; role: 'to' | 'cc' };
}

/** What the modal reports besides the chosen mode, for a recipient add. */
export interface PropagationConfirmExtras {
  role?: 'to' | 'cc';
  /** The ticked saved people (project option only). */
  projectSetContactIds?: string[];
  /** The ticked typed-in rows to save as client contacts too (project option only, R2 AC14). */
  projectSetRowIds?: string[];
}

export interface PropagationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (mode: FieldUpdateMode, extras?: PropagationConfirmExtras) => void | Promise<void>;
  field: PropagationField;
  /** The available modes for this field given current invoice/scope state.
   *  Derived by the caller from R3a meta blocks. Modal renders these as
   *  radio options in the order given. */
  availableModes: FieldUpdateMode[];
  /** Pre-selected radio. Caller picks based on caller's defaults. */
  defaultMode: FieldUpdateMode;
  /** Optional value snapshot for the modal's "from X to Y" copy. */
  oldValue?: string;
  newValue?: string;
  context: PropagationContext;
  /** For star_toggle: simplified single-confirm copy. Caller still uses onConfirm to fire. */
  starToggleAction?: 'add_as_dba_default' | 'remove_from_dba_default';
  /** For star_toggle: set (to the project's label) when the project has people saved of its own. Its set always wins
   *  over the client's default contacts, so the copy says the client default does not reach this project. */
  starProjectLabel?: string;
  /** Why this recipient can or cannot be saved for the project from the current invoice. */
  starProjectSaveState?: 'available' | 'invoice_locked' | 'already_saved' | 'missing_email' | 'unavailable';
  /**
   * Extra consequence line for specific modes, when the caller knows something
   * the static copy table cannot.
   *
   * The table says "{oldValue} won't be auto-included on future {project} 
   * invoices". True, but on the LAST recipient the write does more: it records
   * a deliberate clear, so no future invoice on that project gets automatic
   * recipients AND the org-Primary Attention fallback is turned off for them.
   * For a tenant where 87% of invoices carry no explicit recipients, that is a
   * bigger decision than the sentence admits, and understating reach is the
   * axis she polices (2026-05-28, inv 567/447).
   */
  modeNotes?: Partial<Record<FieldUpdateMode, string>>;
  /** field === 'recipient_add': the role control and the people the project option will save. */
  recipientAdd?: RecipientAddInfo;
}

// ---------- Copy tables ----------

interface RadioCopy {
  title: string;
  body: string;
  /**
   * Optional helper text rendered below `body` in the single-confirm branch.
   * Used by `legal_name / save_to_establishment` to point Christa at the
   * Change Client pencil — her real 2026-05-28 case was a different-client
   * mistake mis-routed via the legal-name pencil. Token substitution is
   * NOT applied — the nudge is plain English, no placeholders.
   *
   * Schema: `{ prefix, cta, suffix }`. `cta` renders as an inline affordance
   * (link-styled span) to draw the eye to the corrective path. Keep null/
   * omit for other (field, mode) combos so the nudge doesn't bleed into
   * them.
   *
   * NOTE: the CTA is currently text-only (not a button) — `BillToPanel`'s
   * Change Client pencil is rendered above the modal but is not in scope
   * to be programmatically focused from inside the modal. Visual affordance
   * + verbatim wording is the v0.14 fix; auto-route-on-click is v0.15+.
   */
  helperText?: {
    prefix: string;
    cta: string;
    suffix: string;
  };
  /**
   * Christa-friction-polish DEF-1 (2026-05-30): Per-invoice legal_name
   * override is deferred to v0.15 (needs a `legal_name_override` column).
   * Until then, name the limit explicitly + give Christa a routing
   * instruction. Rendered as a separate slate caption below `helperText`
   * (NOT inline) so it doesn't compete with the Change Client nudge.
   * LOCKED VERBATIM per friction-map DEF-1.
   */
  secondaryHelperText?: string;
}

/**
 * Substitute {placeholder} tokens with values from `context` + oldValue/newValue.
 * Missing values fall back to a generic placeholder so the copy still reads.
 */
function substitute(template: string, props: PropagationModalProps): string {
  const ctx = props.context;
  const replacements: Record<string, string> = {
    project_name: ctx.project_name || 'this project',
    establishment_name: ctx.establishment_name || 'this DBA',
    organization_name: ctx.organization_name || 'this organization',
    person_name: props.recipientAdd?.personName || 'This person',
    project_label: props.recipientAdd?.projectLabel || ctx.project_name || 'this project',
    affected_invoice_count: String(ctx.affected_invoice_count ?? 0),
    oldValue: props.oldValue || '',
    newValue: props.newValue || '',
  };
  return template.replace(/\{(\w+)\}/g, (match, key) =>
    Object.prototype.hasOwnProperty.call(replacements, key) ? replacements[key] : match,
  );
}

/**
 * (field, mode) -> { title, body } copy table.
 *
 * Strings include {placeholder} tokens that get substituted at render time
 * via `substitute()` above. Locked per spec — adversarial review references
 * these exact strings.
 */
const RADIO_COPY: Partial<Record<PropagationField, Partial<Record<FieldUpdateMode, RadioCopy>>>> = {
  establishment: {
    invoice_only: {
      title: 'Just this invoice',
      body: 'Other {project_name} invoices keep their current DBA.',
    },
    save_to_scope: {
      title: 'This invoice + the project default',
      body: "Future {project_name} invoices will start with {newValue}. Existing draft invoices aren't changed.",
    },
    create_new_scope: {
      title: 'Save as a new project default',
      body: 'Keep {oldValue} as one option. Add {newValue} alongside it.',
    },
  },
  address: {
    invoice_only: {
      title: 'Just this invoice',
      body: 'Other {project_name} invoices keep their current address.',
    },
    save_to_project: {
      title: 'Save to the project',
      body: 'Future {project_name} invoices using the project address will show this address. ({affected_invoice_count} open invoices will update.)',
    },
    save_to_establishment: {
      title: 'Save to {establishment_name}',
      body: 'All invoices using {establishment_name} as their address source will show this. ({affected_invoice_count} affected.)',
    },
    // B4 (v0.14): client-level address default. Writes
    // organizations.address + flips source='organization' so future
    // invoices for ANY DBA under {organization_name} inherit this
    // address by default (christa-email-regression #2 — Pacific
    // Solutions Contracting Ltd / inv 520).
    save_to_organization: {
      title: 'Save as client-level default',
      body: 'All invoices billed to {organization_name} (any DBA) will inherit this address. ({affected_invoice_count} affected.)',
    },
  },
  legal_name: {
    save_to_establishment: {
      title: 'Save to {establishment_name}',
      body: 'All invoices using {establishment_name} will show this legal entity name. ({affected_invoice_count} affected.)',
      // B3 (v0.14, christa-email-regression blocker #1, 2026-05-28):
      // Christa hit "would change 4 others" on legal_name edit and the prior
      // helper pointed her at "edit the DBA" — wrong path for her actual case
      // (she needed a per-invoice client change, not a DBA edit). Re-aim at
      // the Change Client pencil at the top of BillToPanel, with the CTA
      // text styled as an affordance so her eye lands on it.
      helperText: {
        prefix: 'If this is a different client (not a DBA of the current one), cancel and click',
        cta: 'Change Client',
        suffix: 'above.',
      },
      // Christa-friction-polish DEF-1 (2026-05-30): per-invoice legal_name
      // override is deferred to v0.15. Name the limit so users stop
      // self-blaming when they try to do something that isn't supported.
      secondaryHelperText:
        "Need this name on THIS invoice only (not affecting other invoices, the DBA, or the client)? Per-invoice legal entity override isn't supported yet.",
    },
  },
  // SPEC-christa-contacts-identity R1 AC10: plain words, the person by NAME, the project by number and name,
  // no internal terms. "This project, going forward" saves the person for ONE project (one request); the
  // client option is for every project that has no contacts of its own.
  recipient_add: {
    invoice_only: {
      title: 'Just this invoice',
      body: '{person_name} is added to this invoice only.',
    },
    save_project_default: {
      title: 'This project, going forward',
      body: '{person_name} will be on every new invoice for {project_label}. Existing invoices are not changed.',
    },
    save_org_default: {
      title: 'Save as default for {organization_name}',
      body: '{person_name} will be on new invoices for every project billed to {organization_name} that has no contacts saved for the project.',
    },
  },
  recipient_remove: {
    invoice_only: {
      title: 'Just this invoice',
      body: '{oldValue} is removed only from this invoice.',
    },
    // 2026-08-07: mirrors save_dba_default — this removes from
    // project_scope_recipient_defaults (keyed on client_scope_id), so its reach
    // is the PROJECT, not the DBA.
    stop_for_dba: {
      title: 'This project, going forward',
      body: "{oldValue} won't be auto-included on future {project_name} invoices. (To fully delete this contact, manage on the organization page.)",
    },
    stop_for_org: {
      title: 'Stop including for {organization_name}',
      body: "{oldValue} won't be auto-included on any future invoice for {organization_name}.",
    },
  },
};

/**
 * Top-level modal title per field. The `star_toggle` field uses a
 * dedicated title built from `starToggleAction` in `renderTitle()`.
 */
function renderTitle(props: PropagationModalProps): string {
  switch (props.field) {
    case 'establishment':
      return 'Change DBA on this invoice';
    case 'address':
      return 'Change billing address';
    case 'legal_name':
      return 'Change legal entity name';
    case 'recipient_add':
      if (props.recipientAdd?.promote) {
        return substitute('Save {person_name} for {project_label}', props);
      }
      // Never "accounting contact": the project option does not make anybody a client default contact.
      return substitute('Add {newValue} to invoices', props);
    case 'recipient_remove':
      return substitute('Remove {oldValue} from this invoice?', props);
    case 'star_toggle': {
      // Christa-friction-polish F-1 (2026-05-30): when the invoice has no DBA
      // (83% of Banks prod), the BillToPanel actually fires save_org_default
      // / stop_for_org — but the copy here previously substituted
      // "this DBA" via the substitute() default and lied to Christa about
      // what the action does. Branch on whether the context carries a real
      // establishment_name; missing → render org-scoped copy keyed off
      // organization_name.
      const hasDba = !!props.context.establishment_name;
      if (props.starToggleAction === 'add_as_dba_default') {
        return substitute(
          hasDba
            ? 'Save {newValue} as accounting contact for {establishment_name}?'
            : 'Save {newValue} as accounting contact for {organization_name}?',
          props,
        );
      }
      return substitute('Stop auto-including {newValue}?', props);
    }
    default:
      return 'Apply change';
  }
}

/**
 * Optional sub-headline (from→to summary). Returns empty string when not
 * applicable (e.g. star_toggle uses its body copy instead).
 */
function renderSubHeadline(props: PropagationModalProps): string {
  const { field, oldValue, newValue } = props;
  if (field === 'star_toggle') return '';
  if (field === 'recipient_add' && props.recipientAdd?.promote) {
    return substitute('{person_name} is on this invoice for this invoice only. Save them for {project_label}.', props);
  }
  if (field === 'recipient_add' && newValue) {
    return substitute('Adding {newValue} to this invoice.', props);
  }
  if (field === 'recipient_remove' && oldValue) {
    return substitute('Removing {oldValue} from this invoice.', props);
  }
  if (oldValue && newValue) {
    if (field === 'establishment') {
      return substitute("You're changing DBA from {oldValue} to {newValue}.", props);
    }
    if (field === 'address') {
      return substitute("You're changing the billing address.", props);
    }
    if (field === 'legal_name') {
      return substitute(
        "You're changing legal entity name from {oldValue} to {newValue}.",
        props,
      );
    }
  }
  return '';
}

/**
 * Single-confirm body copy used for star_toggle. Mirrors the spec table
 * (star_toggle / add_as_dba_default and remove_from_dba_default rows).
 */
function renderStarToggleBody(props: PropagationModalProps): string {
  // Christa-friction-polish F-1 (2026-05-30): mirror the title-level NULL-DBA
  // branch. When the invoice has no DBA, BillToPanel routes through
  // save_org_default / stop_for_org and the body copy should describe the
  // client-level effect, not "this DBA".
  const hasDba = !!props.context.establishment_name;
  const own = props.starProjectLabel;
  if (own) {
    // The project's own saved people always win over the client's defaults, so say what the star really does.
    const where = hasDba ? '{establishment_name}' : '{organization_name}';
    const label = own.replace(/\{/g, '').replace(/\}/g, '');
    const projectSaveGuidance = props.starProjectSaveState === 'available'
      ? 'To change that, use Save for this project beside {newValue} on this invoice.'
      : props.starProjectSaveState === 'already_saved'
        ? `{newValue} is already saved for ${label}.`
        : props.starProjectSaveState === 'missing_email'
          ? '{newValue} needs an email address before they can be saved for this project.'
          : props.starProjectSaveState === 'invoice_locked'
            ? `To change that, open a Draft or Ready invoice for ${label} that has not been sent. Add {newValue} if needed and save them for this project.`
            : 'This recipient cannot be saved for this project from this invoice.';
    return props.starToggleAction === 'add_as_dba_default'
      ? substitute(`{newValue} will be a default contact for ${where} projects that have no saved people of their own. ${label} has its own saved people, so this does not change who gets ${label}'s next invoice. ${projectSaveGuidance}`, props)
      : substitute(`{newValue} will stop being a default contact for ${where} projects that have no saved people of their own. ${label} keeps its own saved people. Existing invoices aren't changed.`, props);
  }
  if (props.starToggleAction === 'add_as_dba_default') {
    return substitute(
      hasDba
        ? '{newValue} will be auto-included on future {establishment_name} invoices.'
        : '{newValue} will be auto-included on future {organization_name} invoices.',
      props,
    );
  }
  return substitute(
    hasDba
      ? "{newValue} won't be auto-included on future {establishment_name} invoices. Existing invoices aren't changed."
      : "{newValue} won't be auto-included on future {organization_name} invoices. Existing invoices aren't changed.",
    props,
  );
}

// ---------- Component ----------

export function PropagationModal(props: PropagationModalProps) {
  const {
    open,
    onClose,
    onConfirm,
    field,
    availableModes,
    defaultMode,
    starToggleAction,
    modeNotes,
    recipientAdd,
  } = props;

  // Selected radio. Mirrors `defaultMode` until the user picks something
  // else, and is reset whenever the modal closes or `defaultMode` changes
  // (e.g. after a 409 re-render the caller may swap in a new default).
  const [selectedMode, setSelectedMode] = useState<FieldUpdateMode>(defaultMode);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Recipient add only: the person's role, and which saved people are ticked for the project.
  const [role, setRole] = useState<'to' | 'cc'>(recipientAdd?.defaultRole ?? 'to');
  const [tickedIds, setTickedIds] = useState<Set<string>>(
    new Set((recipientAdd?.savedPeople ?? []).map((p) => p.contact_id)),
  );
  const [tickedRows, setTickedRows] = useState<Set<string>>(
    new Set((recipientAdd?.oneOffPeople ?? []).map((p) => p.row_id)),
  );

  /** Polish wave 3 — W3-2 (2026-05-30): synchronous Apply re-entry guard.
   *
   *  Staging re-sweep observed triple-click on this modal's Apply button
   *  firing 3 PUTs to /api/invoices/:id. The writes are idempotent (final
   *  state correct) but the audit trail accumulates 3 rows for one user
   *  intent, and a race condition could in principle produce 412
   *  self-conflicts. `isSubmitting` flips when the async onConfirm starts,
   *  but React batches the re-render — so the 2nd/3rd clicks in a single
   *  millisecond execute their handlers before the DOM `disabled`
   *  attribute updates.
   *
   *  Mirror OrgChangeStepper's B2 pattern: gate `handleConfirm` with a
   *  ref that flips synchronously inside the handler. Released in the
   *  `finally` so legitimate retries (after a real error) still work,
   *  and reset on modal open so a close-without-apply doesn't leave the
   *  button perma-locked.
   */
  const applyInFlightRef = useRef<boolean>(false);

  useEffect(() => {
    if (open) {
      setSelectedMode(defaultMode);
      setIsSubmitting(false);
      setRole(recipientAdd?.defaultRole ?? 'to');
      setTickedIds(new Set((recipientAdd?.savedPeople ?? []).map((p) => p.contact_id)));
      setTickedRows(new Set((recipientAdd?.oneOffPeople ?? []).map((p) => p.row_id)));
      // W3-2: reset on every fresh open so reopening after a
      // close-without-apply doesn't leave the Apply button gated.
      applyInFlightRef.current = false;
    }
    // The role and ticks are seeded from the invoice the modal opens for, not re-seeded on every refresh of it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultMode]);

  // For star_toggle the caller is expected to know the single mode that
  // applies, but the spec only locks the simplified-confirm behavior — we
  // honor `availableModes[0]` if provided, else fall back to a sensible
  // mode based on `starToggleAction`. Either way, fire it on Apply.
  const starToggleMode: FieldUpdateMode = (() => {
    if (availableModes.length > 0) return availableModes[0];
    return starToggleAction === 'add_as_dba_default' ? 'save_dba_default' : 'stop_for_dba';
  })();

  const handleConfirm = async () => {
    // W3-2 race-guard: synchronous re-entry gate. Triple-click on Apply
    // used to fire 3 PUTs because `isSubmitting`'s re-render lagged the
    // same-tick click handlers. The ref flips immediately so subsequent
    // same-tick clicks no-op before they can reach `onConfirm`.
    if (applyInFlightRef.current) return;
    applyInFlightRef.current = true;
    setIsSubmitting(true);
    try {
      const mode = field === 'star_toggle' ? starToggleMode : selectedMode;
      const extras: PropagationConfirmExtras | undefined =
        field === 'recipient_add' && recipientAdd
          ? {
              role: recipientAdd.promote ? recipientAdd.promote.role : role,
              ...(mode === 'save_project_default'
                ? {
                    projectSetContactIds: recipientAdd.savedPeople
                      .filter((p) => tickedIds.has(p.contact_id))
                      .map((p) => p.contact_id),
                    ...(recipientAdd.oneOffPeople
                      ? { projectSetRowIds: recipientAdd.oneOffPeople.filter((p) => tickedRows.has(p.row_id)).map((p) => p.row_id) }
                      : {}),
                  }
                : {}),
            }
          : undefined;
      // Only a recipient add carries extras; every other field reports the mode alone, as it always did.
      if (extras) await onConfirm(mode, extras);
      else await onConfirm(mode);
      // Hotfix 2 — Bug #1: close the modal on successful apply so the user
      // can't double-click into a second PUT. Caller's `pending.apply` is
      // fire-and-forget (mutate, not mutateAsync), so without this the modal
      // would stay open after the click.
      onClose();
    } catch (err) {
      // Hotfix 2 — Bug #1: on error, leave the modal open so the user can
      // retry / cancel. Don't propagate (the click handler is a React event
      // and an unhandled rejection here noisily surfaces in tests + the
      // browser console without the user gaining anything).
      // Surface to console so it's not silently swallowed in dev tooling.
      // eslint-disable-next-line no-console
      console.error('PropagationModal: onConfirm rejected', err);
    } finally {
      setIsSubmitting(false);
      // W3-2: release the synchronous Apply gate once onConfirm resolves
      // (success OR error). On success the modal closes; on error the
      // user can retry after the toast.
      applyInFlightRef.current = false;
    }
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && !isSubmitting) {
      onClose();
    }
  };

  // legal_name with a single option auto-selects and skips the radio group
  // — there is no real choice, so the modal degrades to "Save to X? [Cancel] [Apply]".
  const isSingleConfirm =
    field === 'star_toggle' ||
    (field === 'legal_name' && availableModes.length === 1);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="propagation-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle data-testid="propagation-modal-title">
            {renderTitle(props)}
          </DialogTitle>
          {field === 'star_toggle' ? (
            <DialogDescription data-testid="propagation-modal-subheadline">
              {renderStarToggleBody(props)}
            </DialogDescription>
          ) : (
            renderSubHeadline(props) && (
              <DialogDescription data-testid="propagation-modal-subheadline">
                {renderSubHeadline(props)}
              </DialogDescription>
            )
          )}
        </DialogHeader>

        {/* Body: radios OR single-confirm copy */}
        {isSingleConfirm ? (
          // No radio group — for star_toggle the description above carries the
          // copy. For single-option legal_name we still want to show the
          // single mode's body copy so the user knows what they're confirming,
          // plus an optional helperText nudge (e.g. legal_name → DBA escape path).
          field === 'legal_name' &&
          availableModes[0] &&
          (() => {
            const copy = RADIO_COPY.legal_name?.[availableModes[0]];
            if (!copy) return null;
            const affected = props.context.affected_invoice_count;
            // When count is 0 or undefined, suppress the "(N affected.)"
            // parenthetical — "0 affected" is confusing and the nudge below
            // is forward-looking guidance, not a count of current impact.
            const bodyTemplate =
              !affected
                ? copy.body.replace(/\s*\(\{affected_invoice_count\} affected\.\)/, '')
                : copy.body;
            return (
              <div className="space-y-2">
                <p
                  className="text-sm text-slate-600"
                  data-testid="propagation-modal-single-body"
                >
                  {substitute(bodyTemplate, props)}
                </p>
                {copy.helperText && (
                  <div
                    className="flex items-start gap-2 rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-600"
                    data-testid="propagation-modal-helper-text"
                  >
                    <Info
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400"
                      aria-hidden="true"
                    />
                    <p className="flex-1 leading-relaxed">
                      <span className="font-normal">{copy.helperText.prefix}</span>{' '}
                      <span
                        className="font-semibold text-blue-700 underline underline-offset-2 decoration-blue-300"
                        data-testid="propagation-modal-helper-cta"
                      >
                        {copy.helperText.cta}
                      </span>{' '}
                      <span className="font-normal">{copy.helperText.suffix}</span>
                    </p>
                  </div>
                )}
                {/* Christa-friction-polish DEF-1 (2026-05-30): name the
                    per-invoice override deferral + give the routing
                    instruction. Plain slate caption — does not compete with
                    the Change Client nudge above. */}
                {copy.secondaryHelperText && (
                  <p
                    className="text-xs text-slate-500 italic leading-relaxed px-1"
                    data-testid="propagation-modal-secondary-helper"
                  >
                    {copy.secondaryHelperText}
                  </p>
                )}
              </div>
            );
          })()
        ) : (
          <>
          {field === 'recipient_add' && recipientAdd && !recipientAdd.promote && (
            <fieldset className="space-y-1.5" data-testid="recipient-role-choice">
              <legend className="mb-1 text-sm font-semibold text-slate-900">
                {recipientAdd.personName} is
              </legend>
              <RadioGroup
                value={role}
                onValueChange={(v) => setRole(v as 'to' | 'cc')}
                className="gap-2"
                aria-label={`Role of ${recipientAdd.personName} on this invoice`}
              >
                {(['to', 'cc'] as const).map((r) => (
                  <div key={r} className="flex items-center gap-3">
                    <RadioGroupItem value={r} id={`recipient-role-${r}`} data-testid={`recipient-role-${r}`} />
                    <Label htmlFor={`recipient-role-${r}`} className="cursor-pointer text-sm font-normal text-slate-800">
                      {r === 'to' ? 'Invoices go to (TO)' : 'Copied on (CC)'}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </fieldset>
          )}
          <RadioGroup
            value={selectedMode}
            onValueChange={(v) => setSelectedMode(v as FieldUpdateMode)}
            className="gap-3"
            data-testid="propagation-modal-radio-group"
          >
            {availableModes.map((mode) => {
              const copy = RADIO_COPY[field]?.[mode];
              if (!copy) return null;
              const inputId = `propagation-mode-${mode}`;
              // Address choices backed by a reviewed intent have a more exact
              // consequence than this legacy copy table's automatic blast.
              const reviewedAddressDescription = field === 'address' ? modeNotes?.[mode] : undefined;
              return (
                <div key={mode} className="flex items-start gap-3">
                  <RadioGroupItem
                    value={mode}
                    id={inputId}
                    className="mt-1"
                    data-testid={`propagation-mode-${mode}`}
                  />
                  <Label
                    htmlFor={inputId}
                    className="flex-1 cursor-pointer space-y-1"
                  >
                    <span className="block text-sm font-semibold text-slate-900">
                      {substitute(copy.title, props)}
                    </span>
                    <span className="block text-xs font-normal text-slate-500">
                      {substitute(reviewedAddressDescription || copy.body, props)}
                    </span>
                    {modeNotes?.[mode] && !reviewedAddressDescription && (
                      <span
                        className="block text-xs font-normal text-amber-700"
                        data-testid={`propagation-mode-note-${mode}`}
                      >
                        {substitute(modeNotes[mode] as string, props)}
                      </span>
                    )}
                  </Label>
                </div>
              );
            })}
          </RadioGroup>
          {field === 'recipient_add' && recipientAdd && selectedMode === 'save_project_default' && (
            <div className="space-y-2 rounded-md bg-slate-50 px-3 py-2" data-testid="project-people">
              <p className="text-sm text-slate-700">
                {`These people will be on every new invoice for ${recipientAdd.projectLabel ?? 'this project'}. Other projects are not changed.`}
              </p>
              <ul className="space-y-1.5" data-testid="project-people-list">
                <li className="flex items-center gap-2" data-testid="project-person-new">
                  <Checkbox
                    checked
                    disabled
                    aria-label={`Save ${recipientAdd.personName} for this project`}
                  />
                  <RoleBadge role={recipientAdd.promote ? recipientAdd.promote.role : role} />
                  <span className="text-sm font-medium text-slate-900">{recipientAdd.personName}</span>
                  {recipientAdd.personEmail && <span className="text-xs text-slate-500">{recipientAdd.personEmail}</span>}
                </li>
                {recipientAdd.savedPeople.map((p) => (
                  <li key={p.contact_id} className="flex items-center gap-2" data-testid={`project-person-${p.contact_id}`}>
                    <Checkbox
                      checked={tickedIds.has(p.contact_id)}
                      onCheckedChange={(checked) =>
                        setTickedIds((prev) => {
                          const next = new Set(prev);
                          if (checked === true) next.add(p.contact_id);
                          else next.delete(p.contact_id);
                          return next;
                        })
                      }
                      aria-label={`Save ${p.name} for this project`}
                    />
                    <RoleBadge role={p.role} />
                    <span className="text-sm font-medium text-slate-900">{p.name}</span>
                    {p.email && <span className="text-xs text-slate-500">{p.email}</span>}
                  </li>
                ))}
                {(recipientAdd.oneOffPeople ?? []).map((p) => (
                  <li key={p.row_id} className="flex flex-wrap items-center gap-2" data-testid={`project-oneoff-${p.row_id}`}>
                    <Checkbox
                      checked={tickedRows.has(p.row_id)}
                      onCheckedChange={(checked) =>
                        setTickedRows((prev) => {
                          const next = new Set(prev);
                          if (checked === true) next.add(p.row_id);
                          else next.delete(p.row_id);
                          return next;
                        })
                      }
                      aria-label={`Save ${p.name} for this project`}
                    />
                    <RoleBadge role={p.role} />
                    <span className="text-sm font-medium text-slate-900">{p.name}</span>
                    {p.email && <span className="text-xs text-slate-500">{p.email}</span>}
                    <span className="w-full pl-6 text-xs text-slate-500">
                      {`Saved as a ${props.context.organization_name || 'client'} contact too.`}
                    </span>
                  </li>
                ))}
              </ul>
              {recipientAdd.invoiceOnlyPeople.length > 0 && (
                <div data-testid="project-people-invoice-only">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Stays on this invoice only
                  </p>
                  <ul className="space-y-1">
                    {recipientAdd.invoiceOnlyPeople.map((p) => (
                      <li key={`${p.name}-${p.email}`} className="flex items-center gap-2">
                        <RoleBadge role={p.role} />
                        <span className="text-sm text-slate-700">{p.name}</span>
                        {p.email && <span className="text-xs text-slate-500">{p.email}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          </>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
            data-testid="propagation-modal-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting}
            data-testid="propagation-modal-apply"
          >
            {isSubmitting && (
              <Loader2 className="h-4 w-4 animate-spin" data-testid="propagation-modal-spinner" />
            )}
            {isSubmitting ? 'Applying...' : 'Apply'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
